import React, { useState, useMemo, useEffect } from "react";
import { useProfile, useDeleteAccount } from "../../hooks/useAuth";
import useBiodata from '../../hooks/useBiodata';
import { Alert } from "antd";
import DeleteAccountModal from './DeleteAccountModal';
import { scrollTriggerIntoView } from '../../util/modalUtils';
import { getUserDetail, logoutUser } from '../../util/util';
function getInitialFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  return email.trim()[0]?.toUpperCase() || '';
}

export default function ProfilePage() {
  const { data, isError, refetch, isFetching } = useProfile();
  
  // Force refetch on mount to always get fresh data
  useEffect(() => {
    refetch();
  }, [refetch]);
  const { showDeleteModal, setShowDeleteModal, handleConfirmDelete, handleDeleteSuccess, isDeleting } = useProfileDeleteHandlers();
  // data can be either { success, data } or a plain user object depending on cache
  // Prefer API data; fall back to cached session data when fetch errors
  const sessionUser = getUserDetail();

  // Memoize profile to avoid creating a new object every render which
  // would break useEffect dependency checks (react-hooks/exhaustive-deps).
  const profile = useMemo(() => {
    return data?.data || data || sessionUser || {};
  }, [data, sessionUser]);

  // Determine identifiers for biodata API (auto-mapped). Memoize to
  // keep stable references for downstream hooks/effects.
  // Decode URL-encoded values
  const { employerNumber, staffNumber, canFetchBiodata } = useMemo(() => {
    const e = profile?.EmployerNumber || profile?.employer_number || profile?.employerNumber || profile?.EmployerName || profile?.employer_name;
    const s = profile?.member_no || profile?.member_number || profile?.memberNo || profile?.ssnit_number || profile?.ssnitNumber;
    return { 
      employerNumber: e ? decodeURIComponent(e) : e, 
      staffNumber: s ? decodeURIComponent(s) : s, 
      canFetchBiodata: !!e || !!s 
    };
  }, [profile]);
  // Debug info to help diagnose missing identifiers
  useEffect(() => {
    console.debug('Biodata identifiers', { employerNumber, staffNumber, canFetchBiodata, profile });
  }, [employerNumber, staffNumber, canFetchBiodata, profile]);

  const { data: biodataResp, isLoading: biodataLoading, isError: biodataError, refetch: refetchBiodata } = useBiodata({ employerNumber, staffNumber });
  const memberDetails = biodataResp?.data?.[0] || null;

  // Debug biodata data
  console.log('Biodata debug:', { 
    biodataResp, 
    memberDetails, 
    biodataLoading, 
    biodataError,
    employerNumber, 
    staffNumber 
  });

  // Refetch biodata when profile data changes or identifiers change
  useEffect(() => {
    if (canFetchBiodata && (employerNumber || staffNumber)) {
      refetchBiodata();
    }
  }, [employerNumber, staffNumber, canFetchBiodata, refetchBiodata]);

  // When the delete modal opens, scroll it into view and add keyboard handlers
  useEffect(() => {
    if (!showDeleteModal) return;
    scrollTriggerIntoView();

    const handleKey = (e) => {
      if (e.key === 'Escape') setShowDeleteModal(false);
      if (e.key === 'Enter' && !isDeleting) handleConfirmDelete();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showDeleteModal, isDeleting, setShowDeleteModal, handleConfirmDelete]);

  // If profile fetch errored but we have session data, show it but display a small warning
  const showingStale = !!isError && !!sessionUser;

  if (isError && !sessionUser) {
    return <div className="text-center py-5 text-danger">Failed to load profile.</div>;
  }

  // Only expose compliance-required identifiers
  const profileFields = [
    { key: 'email', label: 'Email', value: profile.email },
    { key: 'phone', label: 'Phone Number', value: profile.phone },
    {
      key: 'ghana_card_id',
      label: 'Ghana Card',
      value: profile.ghana_card_id || profile.ghana_card_number,
    },
    { key: 'ssnit_number', label: 'SSNIT Number', value: profile.ssnit_number },
    { key: 'dob', label: 'Date of Birth', value: profile.dob },
    { key: 'tin', label: 'TIN', value: profile.tin || profile.tin_number },
  ];

  return (
    <div className="container p-0">
          <div className="py-4 d-flex flex-column align-items-center profile-header">
            {showingStale && !isFetching && (
              <div style={{ width: '100%', maxWidth: 800, margin: '0 auto 12px' }}>
                <Alert 
                  type="warning" 
                  message={
                    <span>
                      Unable to refresh profile — displaying cached session data.{' '}
                      <button 
                        onClick={() => refetch()} 
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#1890ff', 
                          cursor: 'pointer', 
                          textDecoration: 'underline',
                          padding: 0
                        }}
                      >
                        Retry
                      </button>
                    </span>
                  } 
                  showIcon 
                  closable 
                />
              </div>
            )}
            {/* {isFetching && !isLoading && (
              <div style={{ width: '100%', maxWidth: 800, margin: '0 auto 12px', textAlign: 'center' }}>
                <Spin size="small" /> <span style={{ marginLeft: 8, color: '#666' }}>Refreshing profile...</span>
              </div>
            )} */}
            {/* Avatar with initials or image */}
            <div className="profile-avatar">
              {/* Show avatar image if available, else initials */}
              {profile.avatar ? (
                <img src={profile.avatar} alt="avatar" className="profile-avatar-img" />
              ) : <span className="profile-initials">{getInitialFromEmail(profile.email)}</span>}
            </div>

            {/* User name */}
            <div className="fw-bold mb-1 profile-name">{profile.name || <span className="text-muted">-</span>}</div>
            {/* Member number under name - decode URL-encoded characters */}
            <div className="mb-2 text-muted profile-member">{profile.member_number ? decodeURIComponent(profile.member_number) : <span className="text-muted">-</span>}</div>

            {/* Profile Details header */}
            <div className="profile-details-title">Profile Details</div>

            {/* Details card */}
            <div className="card p-4 shadow-sm rounded-4 profile-card" style={{ maxWidth: 800, minWidth: 280, width: '100%', color: '#111' }}>
              {profileFields.map(({ key, label, value }) => (
                <ProfileDetail key={key} value={value} label={label} fieldKey={key} />
              ))}
              
              {/* Add biodata fields if available */}
              {memberDetails && (
                <>
                  <ProfileDetail value={memberDetails.MonthlyContribution} label="Monthly Contribution" fieldKey="monthly_contribution" />
                  <ProfileDetail value={memberDetails.EmployerName} label="Employer" fieldKey="employer_name" />
                  <ProfileDetail value={memberDetails.scheme_name} label="Scheme" fieldKey="scheme_name" />
                </>
              )}
              
              <div className="d-flex justify-content-end mt-3">
            <button
  className="btn"
  onClick={() => setShowDeleteModal(true)}
  style={{
    backgroundColor: "#dc3545",
    borderColor: "#dc3545",
    color: "#fff",
    borderRadius: "9999px",   // fully rounded
    padding: "10px 20px"      // better pill shape
  }}
>
  Delete Account
</button>

                
              </div>
            </div>
          </div>
      <DeleteAccountModal visible={showDeleteModal} onCancel={() => setShowDeleteModal(false)} onConfirm={handleConfirmDelete} onSuccess={handleDeleteSuccess} isLoading={isDeleting} />
    </div>
  );
}

// Local state and handlers for deletion modal
function useProfileDeleteHandlers() {
  const { mutateAsync, isLoading } = useDeleteAccount();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleConfirmDelete = async () => {
    return mutateAsync();
  };

  const handleDeleteSuccess = () => {
    logoutUser();
    const navigate = window && window.reactRouterNavigate ? window.reactRouterNavigate : undefined;
    if (navigate) navigate('/onboard/login');
  };

  return { showDeleteModal, setShowDeleteModal, handleConfirmDelete, handleDeleteSuccess, isDeleting: isLoading };
}

// Helper component for detail display
function formatDate(value) {
  if (!value) return null;
  // Accepts both 'YYYY-MM-DD' and ISO strings
  const date = new Date(value);
  if (isNaN(date.getTime())) return value; // fallback to raw value if invalid
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatBooleanLike(value) {
  if (value === null || value === undefined) return null;
  const normalized = value.toString().toLowerCase();
  if (['true', '1', 'yes', 'enabled'].includes(normalized)) return 'Enabled';
  if (['false', '0', 'no', 'disabled'].includes(normalized)) return 'Disabled';
  return value;
}

function formatCurrency(value) {
  if (!value && value !== 0) return null;
  const num = Number(String(value).replace(/[^\d.]/g, ''));
  if (isNaN(num)) return value;
  return `GHS ${num.toLocaleString()}`;
}

function ProfileDetail({ value, label, fieldKey }) {
  const dateFields = ['dob', 'date_joined', 'last_logged_in'];
  const currencyFields = ['monthly_salary', 'monthly_contribution'];
  let displayValue = value;

  if (dateFields.includes(fieldKey) && value) {
    displayValue = formatDate(value);
  }

  if (currencyFields.includes(fieldKey) && value) {
    displayValue = formatCurrency(value);
  }

  if (fieldKey === 'notifications_enabled') {
    displayValue = formatBooleanLike(value) || 'Disabled';
  }

  if (fieldKey === 'terms') {
    displayValue = value || 'Terms not accepted';
  }

  return (
    <div className="profile-row">
      <div className="profile-value">{displayValue || <span className="text-muted">-</span>}</div>
      <div className="profile-label">{label}</div>
    </div>
  );
}

/* Component-scoped styles for responsive behaviour */
const profileStyles = `
  .profile-avatar { width: 110px; height: 110px; border-radius: 50%; background: linear-gradient(135deg, #184b18ff 0%, #162b1bff 100%); display: flex; align-items: center; justify-content: center; font-size: 44px; font-weight: 700; color: #fff; margin-bottom: 16px; box-shadow: 0 2px 12px 0 rgba(50,100,200,0.08); overflow: hidden; }
  .profile-avatar-img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .profile-initials { font-weight: 700; color: #fff; }
  .profile-name { font-size: 24px; }
  .profile-member { font-size: 16px; font-weight: 500; }
  .profile-details-title { font-weight: 700; font-size: 20px; color: #111; text-align: left; max-width: 800px; width: 100%; margin: 8px auto; }
  .profile-card { max-width: 800px; margin: 0 auto; }
  .profile-row { padding: 12px 0; }
  .profile-row:last-child { border-bottom: none; }
  .profile-value { font-size: 18px; font-weight: 700; color: '#111'; text-align: left; line-height: 1.2; }
  .profile-label { font-size: 13px; color: '#111'; border-bottom: 2px solid #e3f0ff; padding-bottom: 2px; margin-top: 6px; font-weight: 400; text-align: left; }

  /* Responsive adjustments */
  @media (max-width: 900px) {
    .profile-avatar { width: 92px; height: 92px; font-size: 36px; }
    .profile-name { font-size: 20px; }
    .profile-value { font-size: 16px; }
    .profile-member { font-size: 14px; }
    .profile-card { padding: 28px; }
    .profile-details-title { font-size: 18px; }
  }

  @media (max-width: 520px) {
    .profile-avatar { width: 72px; height: 72px; font-size: 26px; }
    .profile-name { font-size: 18px; }
    .profile-member { font-size: 13px; }
    .profile-value { font-size: 15px; }
    .profile-card { padding: 16px; border-radius: 12px; }
    .profile-details-title { font-size: 16px; margin-left: 0; }
  }
`;

// Inject styles into the document when module loads (scoped for this file)
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-file', 'ProfilePage.styles');
  styleEl.appendChild(document.createTextNode(profileStyles));
  document.head.appendChild(styleEl);
}
